import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useSelector } from "react-redux";

import { ThemeContext } from "../../context/Themes";
import { AnalyticsService } from "../../services";
import { Heading, Text, Icon } from "../elements";

// 1. Static Theme Palettes
const DARK_COLORS = {
  bg: "#1b2b4d",
  cardBg: "#172442",
  border: "#2f3f61",
  title: "#f0f0f0",
  text: "#d1d1d1",
  green: "#16a34a",
  red: "#c71414",
  blue: "#2351af",
  amber: "#d97706",
};

const LIGHT_COLORS = {
  bg: "#ffffff",
  cardBg: "#f8fafc",
  border: "#e2e8f0",
  title: "#403e57",
  text: "#5e5d72",
  green: "#16a34a",
  red: "#c71414",
  blue: "#2351af",
  amber: "#d97706",
};

interface TaskSummaryChartProps {
  fromDate?: string;
  toDate?: string;
  projectId?: number | string | null;
}

interface SummaryCounts {
  todo: number;
  inProgress: number;
  done: number;
  overdue: number;
}

interface MetricItem {
  key: keyof SummaryCounts;
  label: string;
  count: number;
  color: string;
  bgColor: string;
  iconName: string;
  excludeFromProgress?: boolean;
}

interface RootState {
  user?: {
    user?: {
      company?: {
        id?: number | string;
      };
    };
  };
}

export const TaskSummaryChart: React.FC<TaskSummaryChartProps> = ({ fromDate, toDate, projectId = null }) => {
  const { theme } = useContext(ThemeContext);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [summaryCounts, setSummaryCounts] = useState<SummaryCounts>({
    todo: 0,
    inProgress: 0,
    done: 0,
    overdue: 0,
  });

  const latestRequestId = useRef<number>(0);
  const userDetails = useSelector((state: RootState) => state.user?.user);
  const companyId = userDetails?.company?.id;

  const colors = theme !== "light_mode" ? DARK_COLORS : LIGHT_COLORS;

  const fetchTaskSummary = useCallback(async () => {
    const currentRequestId = ++latestRequestId.current;

    try {
      setLoading(true);
      setError(false);

      const { data } = await AnalyticsService.getCompanyTaskSummary({
        ...(companyId !== undefined && companyId !== null ? { companyId } : {}),
        ...(projectId !== undefined && projectId !== null ? { projectId } : {}),
        ...(fromDate ? { fromDate } : {}),
        ...(toDate ? { toDate } : {}),
      });

      if (currentRequestId !== latestRequestId.current) return;

      setSummaryCounts({
        todo: Number(data?.todo ?? 0),
        inProgress: Number(data?.inProgress ?? 0),
        done: Number(data?.done ?? 0),
        overdue: Number(data?.overdue ?? 0),
      });
    } catch (err) {
      if (currentRequestId === latestRequestId.current) {
        console.error("🚀 ~ fetchTaskSummary ~ error:", err);
        setError(true);
      }
    } finally {
      if (currentRequestId === latestRequestId.current) {
        setLoading(false);
      }
    }
  }, [companyId, projectId, fromDate, toDate]);

  useEffect(() => {
    fetchTaskSummary();
  }, [fetchTaskSummary]);

  const totalTasks = summaryCounts.todo + summaryCounts.inProgress + summaryCounts.done;
  const completionPercentage = totalTasks > 0 ? ((summaryCounts.done / totalTasks) * 100).toFixed(1) : "0.0";

  const metrics: MetricItem[] = [
    {
      key: "todo",
      label: "To Do",
      count: summaryCounts.todo,
      color: colors.amber,
      bgColor: `${colors.amber}22`,
      iconName: "description",
    },
    {
      key: "inProgress",
      label: "In Progress",
      count: summaryCounts.inProgress,
      color: colors.blue,
      bgColor: `${colors.blue}22`,
      iconName: "schedule",
    },
    {
      key: "done",
      label: "Done",
      count: summaryCounts.done,
      color: colors.green,
      bgColor: `${colors.green}22`,
      iconName: "check_circle",
    },
    {
      key: "overdue",
      label: "Overdue",
      count: summaryCounts.overdue,
      color: colors.red,
      bgColor: `${colors.red}22`,
      iconName: "priority_high",
      excludeFromProgress: true,
    },
  ];

  const hasData = !error && (totalTasks > 0 || summaryCounts.overdue > 0);

  return (
    <Box
      className="mc-card"
      sx={{
        backgroundColor: colors.bg,
        border: `1px solid ${colors.border}`,
        borderRadius: "12px",
        p: 2.5,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      {/* 1. Header Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          pb: 2.5,
          mb: 3,
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              backgroundColor: theme !== "light_mode" ? "#22355c" : `${colors.blue}1F`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon
              className="material-icons"
              style={{
                color: "#3b82f6",
                fontSize: "22px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "100%",
                lineHeight: 1,
                margin: 0,
                padding: 0,
              }}
            >
              assignment
            </Icon>
          </Box>

          <Box>
            <Heading
              as="h2"
              style={{
                fontSize: "18px",
                fontWeight: 600,
                color: colors.title,
                letterSpacing: "0.2px",
              }}
            >
              Task Summary
            </Heading>
            <Text
              style={{
                fontSize: "13px",
                color: colors.text,
                marginTop: "4px",
              }}
            >
              Overview of all tasks across the projects
            </Text>
          </Box>
        </Box>

        {/* Total Tasks Badge */}
        <Box
          sx={{
            px: 2.5,
            py: 1,
            borderRadius: "10px",
            backgroundColor: colors.cardBg,
            border: `1px solid ${colors.border}`,
            textAlign: "center",
            minWidth: "90px",
          }}
        >
          <Text
            style={{
              fontSize: "11px",
              color: colors.text,
              display: "block",
              fontWeight: 500,
            }}
          >
            Total Tasks
          </Text>
          <Heading
            as="h4"
            style={{
              fontSize: "20px",
              fontWeight: 700,
              color: "#3b82f6",
              marginTop: "2px",
              lineHeight: 1.1,
            }}
          >
            {hasData ? totalTasks : 0}
          </Heading>
        </Box>
      </Box>

      {/* 2. Main Body Section */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1, py: 8 }}>
          <CircularProgress size={40} />
        </Box>
      ) : hasData ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, flex: 1 }}>
          {/* Progress Bar */}
          <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Text style={{ fontSize: "12px", fontWeight: 600, color: colors.title }}>Overall Progress</Text>
              <Text style={{ fontSize: "12px", fontWeight: 700, color: colors.blue }}>{completionPercentage}% Completed</Text>
            </Box>

            <Box
              sx={{
                height: 8,
                width: "100%",
                borderRadius: "4px",
                backgroundColor: colors.cardBg,
                overflow: "hidden",
                display: "flex",
                gap: "2px",
              }}
            >
              {metrics
                .filter((item) => !item.excludeFromProgress)
                .map((item) => {
                  const pct = totalTasks > 0 ? (item.count / totalTasks) * 100 : 0;
                  if (pct <= 0) return null;
                  return (
                    <Box
                      key={item.key}
                      sx={{
                        width: `${pct}%`,
                        backgroundColor: item.color,
                        height: "100%",
                      }}
                    />
                  );
                })}
            </Box>
          </Box>

          {/* 4 Pillar Cards*/}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" },
              gap: 1.5,
            }}
          >
            {metrics.map((item) => (
              <Box
                key={item.key}
                sx={{
                  p: 2,
                  borderRadius: "8px",
                  backgroundColor: colors.cardBg,
                  border: `1px solid ${colors.border}`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    backgroundColor: item.bgColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1.2,
                    lineHeight: 0,
                    overflow: "hidden",
                  }}
                >
                  <Icon
                    className="material-icons"
                    style={{
                      color: item.color,
                      fontSize: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      height: "100%",
                      lineHeight: 1,
                      margin: 0,
                      padding: 0,
                    }}
                  >
                    {item.iconName}
                  </Icon>
                </Box>

                <Heading as="h3" style={{ fontSize: "22px", fontWeight: 700, color: item.color }}>
                  {item.count}
                </Heading>

                <Text style={{ fontSize: "13px", fontWeight: 500, color: colors.title, marginTop: "4px" }}>{item.label}</Text>
              </Box>
            ))}
          </Box>

          {/* Footer*/}
          <Box
            sx={{
              p: 2.5,
              borderRadius: "14px",
              backgroundColor: colors.cardBg,
              border: `1px solid ${colors.border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  backgroundColor: theme !== "light_mode" ? "#22355c" : `${colors.blue}1F`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Icon
                  className="material-icons"
                  style={{
                    color: "#8280ff",
                    fontSize: "22px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    height: "100%",
                    lineHeight: 1,
                    margin: 0,
                    padding: 0,
                  }}
                >
                  trending_up
                </Icon>
              </Box>

              <Box>
                <Text
                  style={{
                    fontSize: "15px",
                    fontWeight: 600,
                    color: colors.title,
                    letterSpacing: "0.2px",
                  }}
                >
                  Task Completion Rate
                </Text>
                <Text
                  style={{
                    fontSize: "13px",
                    color: colors.text,
                    marginTop: "3px",
                  }}
                >
                  Tasks completed out of total tasks
                </Text>
              </Box>
            </Box>

            <Box sx={{ textAlign: "right" }}>
              <Heading
                as="h4"
                style={{
                  fontSize: "26px",
                  fontWeight: 700,
                  color: "#9b92ff",
                  lineHeight: 1.1,
                }}
              >
                {completionPercentage}%
              </Heading>
              <Text
                style={{
                  fontSize: "13px",
                  color: colors.text,
                  marginTop: "4px",
                  whiteSpace: "nowrap",
                }}
              >
                {summaryCounts.done} of {totalTasks} tasks completed
              </Text>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={{ py: 6, textAlign: "center", flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Text style={{ color: colors.text }}>No task data available</Text>
        </Box>
      )}
    </Box>
  );
};

export default TaskSummaryChart;
